import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { focusedDataKeyAtom } from '../../state';
import { CollateralSummaryChartData } from '../../types';

type CollateralSummaryTranslationProps = {
  warningsText: {
    belowMarginRequirement: string;
    belowWarning: string;
    belowLiquidation: string;
  };
  legendTitles: Record<keyof Omit<CollateralSummaryChartData, 'liquidationDifference' | 'warningDifference'>, string>;
};

export type CollateralSummaryColorProps = {
  differenceTextColor: {
    textPrimary: string;
    belowMarginRequirement: string;
    belowWarning: string;
    belowLiquidation: string;
  };
  legendColors: Record<
    keyof Pick<
      CollateralSummaryChartData,
      | 'assetNonMarginSum'
      | 'collateralValue'
      | 'guarantorAsset'
      | 'positionNonMarginSum'
      | 'liquidation'
      | 'marginRequirement'
      | 'warning'
    >,
    string
  >;
  warningLevels: {
    liquidationLine: string;
    liquidationCircle: string;
    marginRequirementLine: string;
    marginRequirementCircle: string;
    warningLine: string;
    warningCircle: string;
  };
  itemShadow: string;
};

type UserCollateralChartDataKeys = keyof Pick<
  CollateralSummaryChartData,
  'collateralValue' | 'guarantorAsset' | 'assetNonMarginSum' | 'positionNonMarginSum'
>;

const collateralDataOrder: Array<UserCollateralChartDataKeys> = [
  'collateralValue',
  'guarantorAsset',
  'assetNonMarginSum',
  'positionNonMarginSum',
] as const;

export type CollateralSummaryChartDifferenceState = {
  differenceTextColor: string;
  iconColor?: string;
  feedbackDataText?: string;
  feedbackBoxTypeWeb?: 'warning' | 'error'; // This is for the web feedback component only
};

export type CollateralSummaryChartProps = {
  lineWidth: number;
  outerLineRadius: number;
  innerLineRadius: number;
  dotRadius: number;
  borderRadius: number;
  opacityOutOfFocusValue: number;
  chartCXOffset: number;
  chartCYOffset: number;
};

export type UseCollateralSummaryChartCommonProps = {
  collateralData: CollateralSummaryChartData;
  chartProps: CollateralSummaryChartProps;
  colors: CollateralSummaryColorProps;
  translation: CollateralSummaryTranslationProps;
};

export const useCollateralSummaryChartCommon = (props: UseCollateralSummaryChartCommonProps) => {
  const { translation, colors, chartProps, collateralData } = props;
  const {
    borderRadius,
    chartCXOffset,
    chartCYOffset,
    dotRadius,
    innerLineRadius,
    lineWidth,
    opacityOutOfFocusValue,
    outerLineRadius,
  } = chartProps;
  const { marginRequirement, warning, liquidation, totalCollateralValue } = collateralData;
  const [focusedData, setFocusedData] = useAtom(focusedDataKeyAtom);

  const totalCollateralToDifferenceState: CollateralSummaryChartDifferenceState | undefined = useMemo(() => {
    if (!totalCollateralValue) {
      return undefined;
    }

    if (totalCollateralValue > marginRequirement) {
      return { differenceTextColor: colors.differenceTextColor.textPrimary };
    }

    if (totalCollateralValue > warning) {
      return {
        differenceTextColor: colors.differenceTextColor.belowMarginRequirement,
        iconColor: colors.differenceTextColor.belowMarginRequirement,
        feedbackDataText: translation.warningsText.belowMarginRequirement,
        feedbackBoxTypeWeb: 'warning',
      };
    }

    if (totalCollateralValue > liquidation) {
      return {
        differenceTextColor: colors.differenceTextColor.belowWarning,
        iconColor: colors.differenceTextColor.belowWarning,
        feedbackDataText: translation.warningsText.belowWarning,
        feedbackBoxTypeWeb: 'warning',
      };
    }

    return {
      differenceTextColor: colors.differenceTextColor.belowLiquidation,
      iconColor: colors.differenceTextColor.belowLiquidation,
      feedbackDataText: translation.warningsText.belowLiquidation,
      feedbackBoxTypeWeb: 'error',
    };
  }, [totalCollateralValue, marginRequirement, warning, liquidation, colors, translation]);

  // Number between 180 and 0. In this case the value 0 represents a full semicircle. As we increase the value the line will become shorter.
  const collateralChartEndAngle = useMemo(
    () =>
      totalCollateralValue
        ? totalCollateralValue > marginRequirement
          ? 0
          : 180 - 180 * (totalCollateralValue / marginRequirement)
        : 0,
    [marginRequirement, totalCollateralValue],
  );

  const requiredChartEndAngle = useMemo(
    () =>
      totalCollateralValue
        ? marginRequirement > totalCollateralValue
          ? 0
          : 180 - 180 * (marginRequirement / totalCollateralValue)
        : 0,
    [marginRequirement, totalCollateralValue],
  );

  const dotsOffset = useMemo(
    () =>
      totalCollateralValue
        ? marginRequirement > totalCollateralValue
          ? 1
          : marginRequirement / totalCollateralValue
        : 0,
    [marginRequirement, totalCollateralValue],
  );

  const warningDataOrder = useMemo(
    () =>
      [
        {
          key: 'liquidation',
          diffFromPrevValue: liquidation,
        },
        {
          key: 'warning',
          diffFromPrevValue: warning - liquidation,
        },
        {
          key: 'marginRequirement',
          diffFromPrevValue: marginRequirement - warning,
        },
      ] as const,
    [liquidation, marginRequirement, warning],
  );
  // Do not display zero values on chart
  const nonZeroCollateralDataOrder = useMemo(
    () => collateralDataOrder.filter(d => collateralData[d] !== 0),
    [collateralData],
  );

  const nonZeroWarningDataOrderLength = useMemo(
    () => warningDataOrder.filter(d => collateralData[d.key] !== 0).length,
    [collateralData, warningDataOrder],
  );
  const getInnerPieChartItemBorderRadius = useCallback(
    (ind: number) => {
      if (ind === 0) {
        return [
          borderRadius,
          nonZeroCollateralDataOrder.length === 1 ? borderRadius : 0,
          nonZeroWarningDataOrderLength > 0 ? 0 : borderRadius,
          nonZeroCollateralDataOrder.length === 1 ? borderRadius : 0,
        ];
      }

      if (ind === nonZeroCollateralDataOrder.length - 1) {
        return [0, borderRadius, 0, borderRadius];
      }

      return 0;
    },
    [nonZeroCollateralDataOrder.length, nonZeroWarningDataOrderLength, borderRadius],
  );

  // We have to use opacity to hide nullish warning data. If we use filter then we need to set each item's zIndex
  // to ensure the correct order of the dot's overlap. Unfortunately eChart doesn't support zIndex inside itemStyle.
  const getWarningDataOpacity = useCallback(
    (d: keyof CollateralSummaryChartData) => {
      if (collateralData[d] === 0) {
        return 0;
      }

      if (focusedData === undefined || focusedData === d) {
        return 1;
      }

      return opacityOutOfFocusValue;
    },
    [collateralData, focusedData, opacityOutOfFocusValue],
  );

  const outerPieChartData = useMemo(
    () => ({
      type: 'pie',
      radius: [outerLineRadius, outerLineRadius - lineWidth],
      center: [`${100 * chartCXOffset}%`, `${100 * chartCYOffset}%`],
      startAngle: 180,
      endAngle: requiredChartEndAngle,
      label: { show: false },
      data: warningDataOrder.map(d => ({
        name: translation.legendTitles[d.key],
        value: d.diffFromPrevValue,
        itemStyle: {
          color: colors.warningLevels[`${d.key}Line`],
          opacity: getWarningDataOpacity(d.key),
          borderRadius: [0, 0, borderRadius, 0],
        },
      })),
    }),
    [
      getWarningDataOpacity,
      requiredChartEndAngle,
      outerLineRadius,
      lineWidth,
      chartCXOffset,
      chartCYOffset,
      translation,
      colors,
      borderRadius,
      warningDataOrder,
    ],
  );

  const dotData = useMemo(
    () => ({
      type: 'scatter',
      coordinateSystem: 'polar',
      symbol: 'circle',
      symbolSize: lineWidth,
      data: warningDataOrder.map(d => {
        // For the circles the scale goes from -90 to +90, these being the far left and right points of the chart respectively.
        const angle = 180 * (props.collateralData[d.key] / marginRequirement) * dotsOffset - 90;
        return {
          value: [outerLineRadius - dotRadius, angle], // [radius, angle]
          itemStyle: {
            color: colors.warningLevels[`${d.key}Circle`],
            opacity: getWarningDataOpacity(d.key),
          },
          name: translation.legendTitles[d.key],
        };
      }),
    }),
    [
      dotsOffset,
      marginRequirement,
      warningDataOrder,
      lineWidth,
      outerLineRadius,
      dotRadius,
      translation,
      colors,
      getWarningDataOpacity,
      props.collateralData,
    ],
  );

  const innerPieChartSeries = useMemo(
    () => ({
      type: 'pie',
      radius: [innerLineRadius, innerLineRadius - lineWidth],
      center: [`${100 * chartCXOffset}%`, `${100 * chartCYOffset}%`],
      startAngle: 180,
      endAngle: collateralChartEndAngle,
      minAngle: 2,
      data: nonZeroCollateralDataOrder.map((d, ind) => ({
        value: collateralData[d],
        name: d,
        itemStyle: {
          color: colors.legendColors[d],
          opacity: focusedData === undefined || focusedData === d ? 1 : opacityOutOfFocusValue,
          borderRadius: getInnerPieChartItemBorderRadius(ind),
          shadowColor: focusedData === d ? colors.itemShadow : undefined,
          shadowBlur: focusedData === d ? 8 : 0,
        },
        labelLine: {
          show: false,
        },
        label: {
          show: false,
        },
      })),
    }),
    [
      innerLineRadius,
      lineWidth,
      collateralChartEndAngle,
      chartCXOffset,
      chartCYOffset,
      nonZeroCollateralDataOrder,
      collateralData,
      colors,
      opacityOutOfFocusValue,
      focusedData,
      getInnerPieChartItemBorderRadius,
    ],
  );

  const eChartOption = useMemo(
    () => ({
      // 0) polar coordinate system
      polar: {
        center: [`${100 * chartCXOffset}%`, `${100 * chartCYOffset}%`],
        radius: outerLineRadius,
        startAngle: 180,
        endAngle: 360,
      },
      angleAxis: {
        type: 'value',
        clockwise: true,
        show: false,
        min: 0,
        max: 360,
      },
      radiusAxis: {
        type: 'value',
        clockwise: true,
        min: 0,
        // Make the scale go from 0 to the value of the outerLineRadius
        // This will help to offset the dots accurately for different resolutions
        max: outerLineRadius,
        show: false,
      },

      series: [
        // 1) Outer piechart
        outerPieChartData,

        // 2) Circles
        dotData,

        // 3) Inner piechart
        innerPieChartSeries,
      ],
      emphasis: {
        disabled: true,
      },
    }),
    [dotData, innerPieChartSeries, outerPieChartData, outerLineRadius, chartCXOffset, chartCYOffset],
  );

  return {
    totalCollateralToDifferenceState,
    setFocusedData,
    focusedData,
    eChartOption,
  };
};
